import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import {jwtDecode} from 'jwt-decode';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAdd, faFilter, faTimes } from '@fortawesome/free-solid-svg-icons';
import Header from './Header';
import { hostUrl, apiUrl } from './config';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

const ReadResource = ({ baseUrl = hostUrl, staticTable = false, actionsEnabled = true }) => {
    const { resource: resourceParam } = useParams();
    const resource = resourceParam || 'realtime_log';
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [headers, setHeaders] = useState([]);
    const [fieldOptions, setFieldOptions] = useState([]);
    const [pagination, setPagination] = useState({
        count: 0,
        next: null,
        previous: null,
        limit: 50,
        offset: 0,
    });
    

    const [searchQuery, setSearchQuery] = useState('');
    const [selectedField, setSelectedField] = useState('Filter');
    const [filterValue, setFilterValue] = useState('');
    const [period, setPeriod] = useState('all');
    const [customStart, setCustomStart] = useState('');
    const [customEnd, setCustomEnd] = useState('');
    const [isInitialLoadDone, setIsInitialLoadDone] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [wsStatus, setWsStatus] = useState('disconnected');
    const wsRetryRef = useRef({ count: 0, timeoutId: null, shouldReconnect: true });
    const [showReconnectAlert, setShowReconnectAlert] = useState(false);
    const reconnectAlertThreshold = 5; // show alert after this many retries
    const wsRef = useRef(null);
    const tokenRef = useRef(null);

    const getAccessToken = () => localStorage.getItem('access_token');
    const getRefreshToken = () => localStorage.getItem('refresh_token');
    const setAccessToken = (token) => localStorage.setItem('access_token', token);

    const manualRefreshData = () => {
        // Trigger data refetch and WebSocket reconnect
        setRefreshKey(prev => prev + 1);
    };

    const refreshAccessToken = async () => {
        try {
            const response = await axios.post(`${apiUrl}/token/refresh/`, {
                refresh: getRefreshToken(),
            });
            setAccessToken(response.data.access);
            return response.data.access;
        } catch (error) {
            console.error('Failed to refresh access token', error);
            return null;
        }
    };

    // Create axios instance once and reuse it
    const axiosInstanceRef = useRef(null);
    if (!axiosInstanceRef.current) {
        axiosInstanceRef.current = axios.create();

        axiosInstanceRef.current.interceptors.request.use(
            async (config) => {
                let token = getAccessToken();
                if (token) {
                    const decodedToken = jwtDecode(token);
                    const currentTime = Date.now() / 1000;

                    if (decodedToken.exp < currentTime) {
                        token = await refreshAccessToken();
                    }
                    config.headers['Authorization'] = `Bearer ${token}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        axiosInstanceRef.current.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;
                if (error.response && error.response.status === 401 && !originalRequest._retry) {
                    originalRequest._retry = true;
                    const newToken = await refreshAccessToken();
                    if (newToken) {
                        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                        return axiosInstanceRef.current(originalRequest);
                    }
                }
                return Promise.reject(error);
            }
        );
    }

    const axiosInstance = axiosInstanceRef.current;

    // Initialize token once on mount
    useEffect(() => {
        const token = localStorage.getItem('access_token');
        tokenRef.current = token;
    }, []);

    // Clear data and close WebSocket when switching to non-log resources
    useEffect(() => {
        const isLogResource = resource && resource.toLowerCase().includes('log');
        
        // If not a log resource, close WebSocket and clear log data
        if (!isLogResource) {
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
        }
    }, [resource]);

    // Manage WebSocket connection based on resource changes
    useEffect(() => {
        // Close existing WebSocket and clear pending reconnect timers
        if (wsRef.current) {
            wsRef.current.close();
            wsRef.current = null;
        }
        if (wsRetryRef.current.timeoutId) {
            clearTimeout(wsRetryRef.current.timeoutId);
            wsRetryRef.current.timeoutId = null;
        }
        wsRetryRef.current.shouldReconnect = true;

        // Only connect WebSocket if resource contains 'log' and initial data load is done
        if (!resource || !resource.toLowerCase().includes('log') || !isInitialLoadDone) {
            setWsStatus('disconnected');
            return;
        }

        let isMounted = true;

        const connectWs = async () => {
            // Obtain latest token, try refreshing if missing
            let token = getAccessToken();
            console.log('[DEBUG] Initial token check:', token ? 'token exists' : 'no token');
            
            if (!token) {
                console.log('[DEBUG] Token missing, attempting refresh...');
                token = await refreshAccessToken();
                console.log('[DEBUG] After refresh:', token ? 'token obtained' : 'refresh failed');
            }
            if (!token) {
                console.warn('No access token available for WebSocket connection to logs');
                setWsStatus('disconnected');
                return;
            }

            try {
                setWsStatus('connecting');
                const wsProtocol = hostUrl.includes('https') ? 'wss' : 'ws';
                const wsHost = hostUrl.replace('https://', '').replace('http://', '');
                const deviceId = filterValue || 'all';
                const wsUrl = `${wsProtocol}://${wsHost}/ws/device/${deviceId}/logs/?token=${token}`;

                console.log('[DEBUG] WebSocket config:', { wsProtocol, wsHost, deviceId, baseUrl: hostUrl });
                console.log('[DEBUG] Attempting WebSocket connection:', wsUrl);

                const ws = new WebSocket(wsUrl);
                wsRef.current = ws;

                ws.onopen = () => {
                    console.log('[DEBUG] WebSocket onopen fired - connected successfully');
                    if (!isMounted) return;
                    setWsStatus('connected');
                    wsRetryRef.current.count = 0;
                    setShowReconnectAlert(false);
                };

                ws.onmessage = (event) => {
                    try {
                        console.log('[DEBUG] WebSocket message received:', event.data);
                        const logEntry = JSON.parse(event.data);
                        if (!isMounted) return;
                        setData(prevData => [logEntry, ...prevData]);
                        console.log('[DEBUG] New log added to state:', logEntry);
                    } catch (error) {
                        console.error('[DEBUG] Error parsing WebSocket message:', error);
                    }
                };

                ws.onerror = (error) => {
                    console.error('[DEBUG] WebSocket onerror fired:', error);
                    setWsStatus('error');
                };

                ws.onclose = (e) => {
                    console.log('[DEBUG] WebSocket onclose fired:', { code: e.code, reason: e.reason, wasClean: e.wasClean });
                    if (!isMounted) return;
                    setWsStatus('closed');

                    // Attempt reconnect if allowed
                    if (wsRetryRef.current.shouldReconnect) {
                        wsRetryRef.current.count = Math.min(wsRetryRef.current.count + 1, 10);
                        if (wsRetryRef.current.count >= reconnectAlertThreshold) {
                            setShowReconnectAlert(true);
                        }
                        const delay = Math.min(30000, 1000 * Math.pow(2, wsRetryRef.current.count));
                        console.log(`[DEBUG] Scheduling WS reconnect in ${delay}ms (attempt ${wsRetryRef.current.count})`);
                        wsRetryRef.current.timeoutId = setTimeout(() => {
                            wsRetryRef.current.timeoutId = null;
                            connectWs();
                        }, delay);
                    } else {
                        console.log('[DEBUG] WebSocket reconnect disabled');
                    }
                };
            } catch (error) {
                console.error('[DEBUG] Exception during WebSocket connection:', error);
                setWsStatus('error');
            }
        };

        console.log('[DEBUG] useEffect triggered - initiating WebSocket connect', { resource, filterValue, isInitialLoadDone, refreshKey });
        connectWs();

        return () => {
            isMounted = false;
            wsRetryRef.current.shouldReconnect = false;
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
            if (wsRetryRef.current.timeoutId) {
                clearTimeout(wsRetryRef.current.timeoutId);
                wsRetryRef.current.timeoutId = null;
            }
        };
    }, [resource, filterValue, isInitialLoadDone, refreshKey]);

    useEffect(() => {
        // Reset pagination and initial load flag when resource changes
        setPagination(prev => ({
            ...prev,
            offset: 0,
        }));
        setIsInitialLoadDone(false);
    }, [resource]);

    useEffect(() => {
        // Fetch field options when resource changes
        axiosInstance.get(`${baseUrl}/getFields/${resource}/`)
            .then(response => {
                const fieldNames = response.data.fields;
                setFieldOptions(fieldNames);
            })
            .catch(error => {
                console.error('There was an error fetching the field options!', error);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseUrl, resource]);

    useEffect(() => {
        // Fetch data when resource changes (initial load only with defaults)
        const query = new URLSearchParams({
            format: 'json',
            limit: 50,
            offset: 0,
            search: '',
            text_field: 'Filter',
            text_value: '',
            period: resource === 'realtime_log' ? 'all' : undefined,
            custom_start: undefined,
            custom_end: undefined,
        }).toString();

        axiosInstance.get(`${baseUrl}/${resource}/?${query}`)
            .then(response => {
                const responseData = response.data.results;
                setData(responseData);
                setPagination(prev => ({
                    ...prev,
                    count: response.data.count,
                    next: response.data.next,
                    previous: response.data.previous,
                }));

                if (responseData.length > 0) {
                    const keys = Object.keys(responseData[0]);
                    setHeaders(keys);
                }
                setIsInitialLoadDone(true);
            })
            .catch(error => {
                console.error('There was an error fetching the data!', error);
                setIsInitialLoadDone(true);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseUrl, resource]);

    useEffect(() => {
        // Only fetch when pagination or filter changes (after initial load)
        if (!isInitialLoadDone) return;

        const query = new URLSearchParams({
            format: 'json',
            limit: pagination.limit,
            offset: pagination.offset,
            search: searchQuery,
            text_field: selectedField,
            text_value: filterValue,
            period: resource === 'realtime_log' ? period : undefined,
            custom_start: resource === 'realtime_log' ? customStart : undefined,
            custom_end: resource === 'realtime_log' ? customEnd : undefined,
        }).toString();

        axiosInstance.get(`${baseUrl}/${resource}/?${query}`)
            .then(response => {
                const responseData = response.data.results;
                setData(responseData);
                setPagination(prev => ({
                    ...prev,
                    count: response.data.count,
                    next: response.data.next,
                    previous: response.data.previous,
                }));

                if (responseData.length > 0) {
                    const keys = Object.keys(responseData[0]);
                    setHeaders(keys);
                }
            })
            .catch(error => {
                console.error('There was an error fetching the data!', error);
            });
    }, [isInitialLoadDone, pagination.limit, pagination.offset, searchQuery, selectedField, filterValue, period, customStart, customEnd, refreshKey, baseUrl, resource, axiosInstance]);

    const handleNext = () => {
        if (pagination.next) {
            setPagination(prev => ({
                ...prev,
                offset: prev.offset + prev.limit,
            }));
        }
    };

    const handlePrevious = () => {
        if (pagination.previous) {
            setPagination(prev => ({
                ...prev,
                offset: prev.offset - prev.limit,
            }));
        }
    };

    const handlePageChange = (page) => {
        setPagination(prev => ({
            ...prev,
            offset: page * prev.limit,
        }));
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const handleFieldChange = (e) => {
        setSelectedField(e.target.value);
    };

    const handleFilterValueChange = (e) => {
        setFilterValue(e.target.value);
    };

    const handlePeriodChange = (e) => {
        setPeriod(e.target.value);
    };

    const handleCustomStartChange = (e) => {
        setCustomStart(e.target.value);
    };

    const handleCustomEndChange = (e) => {
        setCustomEnd(e.target.value);
    };

    const handleClearFilters = () => {
        setSearchQuery('');
        setSelectedField('Filter');
        setFilterValue('');
        setPeriod('all');
        setCustomStart('');
        setCustomEnd('');
    };

    // const renderPagination = () => {
    //     const totalPages = Math.ceil(pagination.count / pagination.limit);
    //     const currentPage = Math.floor(pagination.offset / pagination.limit);
    //     const pages = [];
    //
    //     for (let i = 0; i < totalPages; i++) {
    //         pages.push(
    //             <button
    //                 key={i}
    //                 className={`btn ${i === currentPage ? 'btn-primary' : 'btn-outline-primary'}`}
    //                 onClick={() => handlePageChange(i)}
    //                 style={{ margin: '0 0.2rem' }}
    //             >
    //                 {i + 1}
    //             </button>
    //         );
    //     }
    //
    //     return pages;
    // };

    const renderPagination = () => {
    const totalPages = Math.ceil(pagination.count / pagination.limit);
    const currentPage = Math.floor(pagination.offset / pagination.limit);
    const pages = [];
    const maxVisiblePages = 2; // Number of leading/trailing pages to show

    // Function to add a page button
    const addPageButton = (pageNumber) => {
        pages.push(
            <button
                key={pageNumber}
                className={`btn ${pageNumber === currentPage ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => handlePageChange(pageNumber)}
                style={{ margin: '0 0.2rem' }}
            >
                {pageNumber + 1}
            </button>
        );
    };

    // Show first two pages
    for (let i = 0; i < Math.min(maxVisiblePages, totalPages); i++) {
        addPageButton(i);
    }

    // Show ellipsis if there is a gap between first pages and current page range
    if (currentPage > maxVisiblePages) {
        pages.push(<span key="ellipsis1">...</span>);
    }

    // Show the current page and its neighbors
    for (
        let i = Math.max(maxVisiblePages, currentPage - 1);
        i <= Math.min(totalPages - maxVisiblePages - 1, currentPage + 1);
        i++
    ) {
        addPageButton(i);
    }

    // Show ellipsis if there is a gap before last pages
    if (currentPage < totalPages - maxVisiblePages - 2) {
        pages.push(<span key="ellipsis2">...</span>);
    }

    // Show last two pages
    for (let i = Math.max(totalPages - maxVisiblePages, maxVisiblePages); i < totalPages; i++) {
        addPageButton(i);
    }

    return pages;
};

    const getRowHighlightClass = (item) => {
        /**
         * Determine row highlight color based on anomaly or error content
         * Returns: 'alert-critical', 'alert-warning', 'alert-info', or ''
         */
        const errorMsg = String(item.error || '').toLowerCase();
        const anomaly = item.anomaly === true || item.anomaly === 'true';
        
        // Critical alerts - RED
        if (anomaly || errorMsg.includes('alert') || errorMsg.includes('error') || 
            errorMsg.includes('offline') || errorMsg.includes('power') || 
            errorMsg.includes('vacuum') || errorMsg.includes('critical')) {
            return 'table-row-alert-critical';
        }
        
        // Warning alerts - YELLOW
        if (errorMsg.includes('warning') || errorMsg.includes('battery') || 
            errorMsg.includes('low') || errorMsg.includes('high')) {
            return 'table-row-alert-warning';
        }
        
        // Info alerts - BLUE
        if (errorMsg && errorMsg !== '') {
            return 'table-row-alert-info';
        }
        
        return '';
    };

    const formatTimestamp = (timestamp) => {
        /**
         * Convert timestamp to relative time format (e.g., "2 minutes ago")
         */
        if (!timestamp) return 'N/A';
        
        try {
            const date = new Date(timestamp);
            const now = new Date();
            const seconds = Math.floor((now - date) / 1000);
            
            if (seconds < 60) return `${seconds} second${seconds !== 1 ? 's' : ''} ago`;
            
            const minutes = Math.floor(seconds / 60);
            if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
            
            const hours = Math.floor(minutes / 60);
            if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
            
            const days = Math.floor(hours / 24);
            if (days < 30) return `${days} day${days !== 1 ? 's' : ''} ago`;
            
            const months = Math.floor(days / 30);
            if (months < 12) return `${months} month${months !== 1 ? 's' : ''} ago`;
            
            const years = Math.floor(months / 12);
            return `${years} year${years !== 1 ? 's' : ''} ago`;
        } catch (error) {
            return String(timestamp);
        }
    };

    // Device-specific column configuration
    const deviceColumns = [
        'id',
        'address',
        'name',
        'serial_number',
        'esim',
        'sensor_type',
        'latitude',
        'longitude',
        'installation_date',
        'subscription_end_date',
        'error',
        'status',
        'last_data_timestamp'
    ];

    // Custom_user-specific column configuration
    const customUserColumns = [
        'id',
        'name',
        'email',
        'phone_number',
        'organization name',
        'usernames'
    ];

    const deviceColumnLabels = {
        'id': 'ID',
        'address': 'Address',
        'name': 'Name',
        'serial_number': 'Serial',
        'esim': 'ESIM',
        'sensor_type': 'Sensor',
        'latitude': 'LA',
        'longitude': 'Lo',
        'installation_date': 'Installed',
        'subscription_end_date': 'Sub.End',
        'error': 'Alert',
        'status': 'Status',
        'last_data_timestamp': 'Last Reported'
    };

    const customUserColumnLabels = {
        'id': 'ID',
        'name': 'Name',
        'email': 'Email',
        'phone_number': 'Phone',
        'organization name': 'Organization',
        'usernames': 'Username'
    };

    const getDisplayHeaders = () => {
        if (resource === 'device') {
            return deviceColumns;
        }
        if (resource === 'custom_user') {
            return customUserColumns;
        }
        return headers;
    };

    const formatHeader = (header) => {
        if (resource === 'device' && deviceColumnLabels[header]) {
            return deviceColumnLabels[header];
        }
        if (resource === 'custom_user' && customUserColumnLabels[header]) {
            return customUserColumnLabels[header];
        }
        return header.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
    };

    const formatCellValue = (header, value) => {
        /**
         * Format cell values based on header type
         */
        if (!value && value !== 0) return '---';
        
        // Installation date and subscription end date - format as dd-mm-yyyy
        if (['installation_date', 'subscription_end_date'].includes(header)) {
            try {
                const date = new Date(value);
                if (isNaN(date.getTime())) return String(value);
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                return `${day}-${month}-${year}`;
            } catch (error) {
                return String(value);
            }
        }

        // Last data timestamp - show relative time
        if (['ts', 'last_data_timestamp'].includes(header)) {
            return formatTimestamp(value);
        }

        // Latitude/Longitude formatting
        if (['latitude', 'longitude'].includes(header)) {
            const num = parseFloat(value);
            return isNaN(num) ? value : num.toFixed(2);
        }

        // Error/Alert formatting
        if (header === 'error') {
            const error = String(value).toLowerCase().trim();
            if (error === '' || error === 'none') return '---';
            return String(value);
        }

        // Status formatting
        if (header === 'status') {
            const status = String(value).toLowerCase().trim();
            if (status === 'ok' || status === '') return 'Normal';
            return String(value);
        }

        // Address - truncate if too long
        if (header === 'address') {
            const addr = String(value);
            return addr.length > 20 ? addr.substring(0, 20) + '...' : addr;
        }

        return String(value).replace(",", "  ");
    };

    const user = JSON.parse(localStorage.getItem('user'));
    const [sidebarWidth, setSidebarWidth] = useState(250);

    useEffect(() => {
        // Listen for sidebar collapse/expand changes
        const handleSidebarChange = () => {
            const sidebar = document.querySelector('.sidebar');
            if (sidebar) {
                const width = sidebar.classList.contains('collapsed') ? 80 : 250;
                setSidebarWidth(width);
            }
        };

        // Check initial state
        handleSidebarChange();

        // Listen for class changes on sidebar
        const sidebar = document.querySelector('.sidebar');
        if (sidebar) {
            const observer = new MutationObserver(handleSidebarChange);
            observer.observe(sidebar, { attributes: true, attributeFilter: ['class'] });
            return () => observer.disconnect();
        }
    }, []);

    return (
        <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
            <Header />
            <main style={{ 
                marginLeft: `${sidebarWidth}px`,
                marginTop: '90px',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                transition: 'margin-left 0.3s ease',
                width: `calc(100% - ${sidebarWidth}px)`
            }}>
                <div className="d-flex flex-column flex-grow-1" style={{ overflow: 'hidden' }}>
                    <div className="content p-2" style={{ flex: '0 0 auto', maxWidth: '100%', paddingTop: '20px' }}>
                        <div className="container-fluid mt-3">
                            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap border-bottom border-1">
                                <div className="p-2 flex-grow-1 text-center">
                                    <h3 className="mb-0" style={{ fontSize: '1.5rem', color: '#333' }}>
                                        {resource.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())}
                                        {resource === 'realtime_log' && (
                                            <small style={{ marginLeft: '0.75rem', fontSize: '0.75rem', color: wsStatus === 'connected' ? 'green' : wsStatus === 'connecting' ? '#f0ad4e' : 'red' }}>
                                                {wsStatus}
                                            </small>
                                        )}
                                    </h3>
                                </div>
                                <div className="p-2">
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip id={`tooltip-search`}>Type here to search</Tooltip>}
                                    >
                                        <input
                                            id="searchQuery"
                                            type="text"
                                            className="form-control"
                                            placeholder="Search..."
                                            value={searchQuery}
                                            onChange={handleSearchChange}
                                            style={{ padding: '0.5rem', transition: 'all 0.3s ease' }}
                                        />
                                    </OverlayTrigger>
                                </div>
                                <div className="p-2">
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip id={`tooltip-select`}>Select a field to filter by</Tooltip>}
                                    >
                                        <select
                                            id="selectedField"
                                            className="form-control"
                                            value={selectedField}
                                            onChange={handleFieldChange}
                                            style={{
                                                padding: '0.5rem',
                                                transition: 'all 0.3s ease',
                                                background: '#f8f9fa',
                                                border: '1px solid #ced4da',
                                                color: '#495057',
                                                appearance: 'none',
                                                backgroundImage: 'url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAiIGhlaWdodD0iOCIgdmlld0JveD0iMCAwIDEwIDgiIHZlcnNpb249IjEuMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBvbHlnb24gcG9pbnRzPSIwLDAgNSw1IDEwLDBaIiBmaWxsPSIjNjc3IiBzdHJva2U9IiM2NzciIHN0cm9rZS13aWR0aD0iMCIvPgo8cG9seWdvbiBwb2ludHM9IjAsOCAtNSwzIC01LDgiIGZpbGw9IiM2NzciIHN0cm9rZT0iIzY3NyIgc3Ryb2tlLXdpZHRoPSIwIi8+Cjwvc3ZnPgo=)',
                                                backgroundRepeat: 'no-repeat',
                                                backgroundPosition: 'right 0.75rem center',
                                                backgroundSize: '8px 10px'
                                            }}
                                        >
                                            <option value="Filter">Filter</option>
                                            {fieldOptions.map((option, index) => (
                                                <option key={index} value={option}>
                                                    {option}
                                                </option>
                                            ))}
                                        </select>
                                    </OverlayTrigger>
                                </div>
                                <div className="p-2">
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip id={`tooltip-filter`}>Type value to filter by</Tooltip>}
                                    >
                                        <input
                                            id="filterValue"
                                            type="text"
                                            className="form-control"
                                            placeholder="Filter value..."
                                            value={filterValue}
                                            onChange={handleFilterValueChange}
                                            style={{ padding: '0.5rem', transition: 'all 0.3s ease' }}
                                        />
                                    </OverlayTrigger>
                                </div>
                                {resource === 'realtime_log' && (
                                    <div className="p-2">
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={<Tooltip id={`tooltip-period`}>Select a timeline</Tooltip>}
                                        >
                                            <select
                                                id="period"
                                                className="form-control"
                                                value={period}
                                                onChange={handlePeriodChange}
                                                style={{ padding: '0.5rem', transition: 'all 0.3s ease' }}
                                            >
                                                <option value="all">All</option>
                                                <option value="24hours">Last 24 Hours</option>
                                                <option value="weekly">Weekly</option>
                                                <option value="fortnightly">Fortnightly</option>
                                                <option value="monthly">Monthly</option>
                                                <option value="custom">Custom Range</option>
                                            </select>
                                        </OverlayTrigger>
                                    </div>
                                )}
                                {period === 'custom' && (
                                    <div className="p-2 d-flex" style={{ flexWrap: 'wrap' }}>
                                        <div className="p-2">
                                            <input
                                                id="customStart"
                                                type="date"
                                                className="form-control"
                                                value={customStart}
                                                onChange={handleCustomStartChange}
                                                style={{ padding: '0.5rem', transition: 'all 0.3s ease' }}
                                            />
                                        </div>
                                        <div className="p-2">
                                            <input
                                                id="customEnd"
                                                type="date"
                                                className="form-control"
                                                value={customEnd}
                                                onChange={handleCustomEndChange}
                                                style={{ padding: '0.5rem', transition: 'all 0.3s ease' }}
                                            />
                                        </div>
                                    </div>
                                )}
                                <div className="p-2 d-flex align-items-end me-2">
                                    <button className="btn btn-primary me-2" onClick={manualRefreshData}>
                                        <FontAwesomeIcon icon={faFilter} />
                                    </button>
                                    <button className="btn btn-secondary" onClick={handleClearFilters}>
                                        <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                </div>
                                {actionsEnabled && !staticTable && (user?.is_staff || user?.is_superuser) && (
                                    <div className="p-2">
                                        <Link className="btn btn-primary" to={`/${resource}/create/`}>
                                            <FontAwesomeIcon icon={faAdd} /> Add New
                                        </Link>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    {showReconnectAlert && (
                        <div className="container-fluid mt-2">
                            <div className="alert alert-warning alert-dismissible d-flex justify-content-between align-items-center" role="alert">
                                <div>
                                    <strong>Connection issues:</strong> Unable to receive live logs. Reconnecting in background.
                                </div>
                                <div>
                                    <button className="btn btn-sm btn-outline-primary me-2" onClick={() => { setShowReconnectAlert(false); setRefreshKey(prev => prev + 1); }}>
                                        Reconnect now
                                    </button>
                                    <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowReconnectAlert(false)}></button>
                                </div>
                            </div>
                        </div>
                    )}
                    <div style={{ flex: 1, overflow: 'auto' }}>
                        <table className="table table-striped table-hover" style={{ minWidth: '100%' }}>
                            <thead className="thead-dark">
                                <tr>
                                    {(resource === 'device' ? deviceColumns : resource === 'custom_user' ? customUserColumns : headers).map((header, index) => (
                                        <th key={index} scope="col" style={{ padding: '0.75rem', fontWeight: 'bold' }}>
                                            {formatHeader(header)}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {data.map((item, index) => (
                                    <tr 
                                        key={index}
                                        className={getRowHighlightClass(item)}
                                        style={{ cursor: actionsEnabled && !staticTable ? 'pointer' : 'default' }}
                                        onClick={actionsEnabled && !staticTable ? () => navigate(`/${resource}/update/${item.id}`) : undefined}
                                    >
                                        {(resource === 'device' ? deviceColumns : resource === 'custom_user' ? customUserColumns : headers).map((header, headerIndex) => (
                                            <td key={headerIndex} style={{ padding: '0.75rem', verticalAlign: 'middle' }}>
                                                {formatCellValue(header, item[header])}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mt-3" style={{ padding: '0.5rem' }}>
                        <button className="btn btn-primary" onClick={handlePrevious} disabled={!pagination.previous}>
                            Previous
                        </button>
                        <div className="pagination-buttons" style={{ display: 'flex', gap: '0.5rem' }}>
                            {renderPagination()}
                        </div>
                        <button className="btn btn-primary" onClick={handleNext} disabled={!pagination.next}>
                            Next
                        </button>
                    </div>
                </div>
            </main>
        </div>
    );
};


export default ReadResource;